import React, { useEffect, useMemo, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Typography,
  List,
  ListItem,
  Tooltip,
  Modal,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { bgcolor, styled } from "@mui/system";
import { useForm } from "react-hook-form";
import axios from "axios";
import { CheckToken } from "../../login/AccessToken";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { ChangeCursor } from "./MiscFunctions";
import { Card, Col, Container, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { Socket } from "../../../components/Socket";
import UploadReprortTable from "./UploadReprortTable";
import UploadReportFilePreview from "./UploadReportFilePreview";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { usePermissions } from "../../login/PermissionContext";
import DiagnosticReportsUnderwriter from "../../utils/DiagnosticReportsUnderwriter";
import { displayPatientName } from "../../../utils/displayPatientName";

const REACT_APP_SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const randomId = uuidv4();

const guessUploadMimeType = (fileName, contentType) => {
  if (contentType && contentType !== "binary/octet-stream") {
    return contentType;
  }
  const lower = String(fileName || "").toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".zip")) return "application/zip";
  return "application/octet-stream";
};

const fileBase64ToDataUrl = (file) => {
  if (!file?.file_base64) return null;
  const mimeType = guessUploadMimeType(file.file_name, file.content_type);
  const raw = String(file.file_base64).replace(/\s/g, "");
  if (raw.startsWith("data:")) return raw;
  return `data:${mimeType};base64,${raw}`;
};

const getS3PreviewSrc = (file) => {
  const mimeType = guessUploadMimeType(file?.file_name, file?.content_type);
  if (mimeType.startsWith("image/")) {
    const dataUrl = fileBase64ToDataUrl(file);
    if (dataUrl) return dataUrl;
  }
  return file?.url || null;
};

const isImageUploadName = (fileName, contentType) => {
  const mime = guessUploadMimeType(fileName, contentType);
  return mime.startsWith("image/");
};

const dataUrlToFile = async (dataUrl, fileName, mimeType) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName || "document", {
    type: mimeType || blob.type || "application/octet-stream",
  });
};

const urlToFile = async (url, fileName, mimeType) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to fetch file from S3");
  const blob = await response.blob();
  return new File([blob], fileName || "document", {
    type: mimeType || blob.type || "application/octet-stream",
  });
};

const matchesAcceptedExtension = (fileName, acceptedFileTypes) => {
  const lower = String(fileName || "").toLowerCase();
  const accepted = String(acceptedFileTypes || "")
    .toLowerCase()
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (accepted.length === 0) return true;
  return accepted.some((ext) => {
    const normalized = ext.startsWith(".") ? ext : `.${ext}`;
    return lower.endsWith(normalized);
  });
};

/** Detect kind for S3 docs (name / mime / base64 magic). */
const detectUploadedDocKind = (file) => {
  const name = String(file?.file_name || "").toLowerCase();
  const mime = guessUploadMimeType(file?.file_name, file?.content_type);
  if (name.endsWith(".pdf") || mime === "application/pdf") return "pdf";
  if (name.endsWith(".zip") || mime.includes("zip")) return "zip";
  if (
    /\.(png|jpe?g|gif|webp|bmp)$/i.test(name) ||
    mime.startsWith("image/")
  ) {
    return "image";
  }
  const b64 = String(file?.file_base64 || "")
    .replace(/\s/g, "")
    .replace(/^data:[^;]+;base64,/i, "");
  if (b64.startsWith("JVBERi0")) return "pdf";
  if (b64.startsWith("UEsDB")) return "zip";
  if (b64.startsWith("/9j/") || b64.startsWith("iVBORw0")) return "image";
  return "other";
};

const isSelectableS3Doc = (file, acceptedFileTypes) => {
  const kind = detectUploadedDocKind(file);
  const accepted = String(acceptedFileTypes || "").toLowerCase();
  if (accepted.includes("pdf")) return kind === "pdf";
  if (accepted.includes("zip")) return kind === "zip";
  return true;
};

// Open a WebSocket connection with the random ID
// const ws = new WebSocket(`${REACT_APP_SOCKET_URL}?${randomId}`);

function UploadReport() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    setValue,
    reset,
    clearErrors,
  } = useForm();
  const [userRole, setUserRole] = useState(sessionStorage.getItem("roleType"));
  const [submitted, setSubmitted] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const [HIPTypes, setHIPTypes] = useState([]);
  const [DataTypes, setDataTypes] = useState([
    "Structured Data",
    "Unstructured Data",
  ]);

  const [currentPatient, setCurrentPatient] = useState();
  const [care_context_list, setCare_context_list] = useState([]);
  const [patientList, setPatientList] = useState([]);
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const [selectedPatient, setSelectedPatient] = useState();

  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const [fileNameSelected, setFileNameSelected] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const history = useHistory();
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const REACT_APP_SUFFIX =
    process.env.REACT_APP_IS_ABDM_CONNECTED_WITH_PROD === "true"
      ? process.env.REACT_APP_SUFFIX_PR
      : process.env.REACT_APP_SUFFIX_SB;
  const [refresh, setRefresh] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedRefId, setSelectedRefId] = useState("");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewModalFile, setPreviewModalFile] = useState(null);
  const [isS3ModalOpen, setIsS3ModalOpen] = useState(false);
  const [s3Files, setS3Files] = useState([]);
  const [s3SearchQuery, setS3SearchQuery] = useState("");
  const [isFetchingS3Docs, setIsFetchingS3Docs] = useState(false);
  const [isImportingS3, setIsImportingS3] = useState(false);
  const [selectedS3File, setSelectedS3File] = useState(null);
  // NEW STATE: Track ABDM link status
  const [linkToABDM, setLinkToABDM] = useState(false);
  // NEW STATE: Phone search
  const [phoneSearchResults, setPhoneSearchResults] = useState([]);
  const [isSearchingPatient, setIsSearchingPatient] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const { userInfo } = usePermissions();
  const isABDMEnabled = userInfo.abdm_enabled;
  const isAIEnabled = userInfo.ai_enabled;
  const handleCopy = () => {
    toast.success("Copied to clipboard");
    if (selectedRefId) {
      // alert("Called with value", selectedRefId);
      navigator.clipboard
        .writeText(selectedRefId)
        .then(() => setCopied(true))
        .catch((err) => console.error("Failed to copy text: ", err));

      // Reset the "Copied" message after a delay
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleRefresh = () => {
    setRefresh(!refresh);
  };

  // NEW FUNCTION: Handle checkbox toggle
  const handleLinkToggle = (e) => {
    setLinkToABDM(e.target.checked);
    if (!e.target.checked) {
      // Clear ABHA field when unchecked
      setValue("abha_id", "");
    }
    // Clear phone search state when toggling modes
    setPhoneSearchResults([]);
    setValue("phone_number", "");
  };

  // NEW FUNCTION: Search patient by phone number
  const searchPatientByPhone = () => {
    const phone = getValues("phone_number");
    if (!phone || phone.trim().length < 8) {
      toast.error("Enter a valid phone number.");
      return;
    }
    if (!Token()) return;
    setIsSearchingPatient(true);
    const reqBody = {
      filter_type: "phone_number",
      filter_details: { phone_number: phone },
      multipleResults: true,
    };
    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
        JSON.stringify(reqBody),
        { headers }
      )
      .then((res) => {
        setIsSearchingPatient(false);
        const data = res.data;
        if (
          data?.status === "Success" &&
          data?.is_valid &&
          Array.isArray(data?.patient_details) &&
          data.patient_details.length > 0
        ) {
          if (data.patient_details.length === 1) {
            const patient = data.patient_details[0];
            setValue("patient_id", patient.patient_id, {
              shouldValidate: true,
            });
            setCurrentPatient(patient);
            setPhoneSearchResults([]);
            toast.success("Patient found and selected");
          } else {
            setPhoneSearchResults(data.patient_details);
            setValue("patient_id", "", { shouldValidate: true });
            setCurrentPatient(undefined);
            toast.info("Multiple patients found. Please select one.");
          }
        } else {
          setPhoneSearchResults([]);
          setValue("patient_id", "", { shouldValidate: true });
          toast.error("No patient found for this phone number");
        }
      })
      .catch((err) => {
        setIsSearchingPatient(false);
        setPhoneSearchResults([]);
        console.log("Phone search error:", err);
        toast.error("Failed to search patient by phone number");
      });
  };

  const handleSelectPatientFromResults = (e) => {
    const selectedId = e.target.value;
    setValue("patient_id", selectedId, { shouldValidate: true });
    const patient = phoneSearchResults.find((p) => p.patient_id === selectedId);
    if (patient) setCurrentPatient(patient);
  };

  useEffect(() => {
    console.log("Hi i am abdm");
    setLinkToABDM(isABDMEnabled); //if ABDM enabled, then link to ABDM will be true by default
  }, []);

  useEffect(() => {
    if (Token()) {
      axios
        .get(
          `${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`,
          {
            headers: headers,
          }
        )
        .then((res) => {
          setPatientList(res.data?.data);
        });
    }
  }, [authToken]);
  console.log("currentPatient", currentPatient);

  useEffect(() => {
    if (
      sessionStorage.getItem("accessToken") == undefined &&
      sessionStorage.getItem("accessToken") == null
    ) {
      history.push("/login");
    }
    if (
      sessionStorage.getItem("subscriptionType") !== undefined &&
      sessionStorage.getItem("subscriptionType") !== null &&
      sessionStorage.getItem("subscriptionType").includes("Connector/Adaptor")
    ) {
      history.push("/hlthtek-demo");
    }
    setValue("facility_id", sessionStorage.getItem("facility_name"));
  }, []);

  const Token = () => {
    const check = CheckToken();
    setAuthToken(sessionStorage.getItem("accessToken"));
    return check;
  };

  useEffect(() => {
    var res = [];
    if (isAIEnabled) {
      res = [
        "Prescription",
        "Quantitative Lab Report (OCR based)",
        "Diagnostic Report",
        "OP Consultation",
        "Discharge Summary",
        "Immunization Record",
        "Health Document",
        "Wellness Record",
      ];
    } else {
      res = [
        "Prescription",
        "Diagnostic Report",
        "OP Consultation",
        "Discharge Summary",
        "Immunization Record",
        "Health Document",
        "Wellness Record",
      ];

    }
    setHIPTypes(res);
  }, [isAIEnabled]);

  const onChangeHIP = () => {
    setFileTypes(["LIPID-PROFILE", "BLOOD-REPORT", "X-Ray", "USG", "CT Scan"]);
  };

  const downloadTemplate = () => {
    //ChangeCursor();
    const mapHiTypes = {
      Prescription: "prescription",
      "Quantitative Lab Report (OCR based)": "diagnostic_report",
      "Diagnostic Report": "diagnostic_report",
      "OP Consultation": "op_consultation",
      "Discharge Summary": "discharge_summary",
      "Immunization Record": "immunization_record",
      "Health Document": "health_document",
      "Wellness Record": "wellness_record",
    };
    if (Token()) {
      // const body = { hi_type: getValues("health_info_type") };
      const selectedType = getValues("health_info_type");

      if (!selectedType) {
        toast.error("Select Health Info type to download the template");
        return;
      }
      if (selectedType === "Health Document") {
        toast.error("Template download not available for 'Health Document'");
        return;
      }

      const mappedType = mapHiTypes[selectedType];
      const body = { hi_type: mappedType };

      if (getValues("health_info_type") === null) {
        toast.error("Select Health Info type to download the template");
      }
      // else if (getValues("file_type") === null) {
      //   toast.error("Select File type to download the template");
      // }
      else {
        axios
          .post(`${REACT_APP_BACKEND_ROUTE_URL}get_hi_type_xlsx_files/`, body, {
            headers: headers,
          })
          // .then((response) => {
          //   var blob = b64toBlob(response.data["body"], "application/zip");
          //   const url = window.URL.createObjectURL(blob, {
          //     type: "application/zip",
          //   });
          //   const link = document.createElement("a");
          //   link.href = url;
          //   link.setAttribute("download", `${getValues("file_type")}.zip`);
          //   document.body.appendChild(link);
          //   link.click();
          //   //ChangeCursor();
          // })

          .then((response) => {
            if (
              response.data.status == "Success" &&
              response.data.message == "Files retrieved successfully"
            ) {
              const { master_xlsx_url, detail_xlsx_url } = response.data;

              // Download Master.xlsx
              const link1 = document.createElement("a");
              link1.href = master_xlsx_url;
              link1.setAttribute("download", "Master.xlsx");
              link1.setAttribute("target", "_blank");
              document.body.appendChild(link1);
              link1.click();
              link1.remove();

              // Download Detail.xlsx
              const link2 = document.createElement("a");
              link2.href = detail_xlsx_url;
              link2.setAttribute("download", "Detail.xlsx");
              link2.setAttribute("target", "_blank");
              document.body.appendChild(link2);
              link2.click();
              link2.remove();
            } else {
              toast.error("error in downloading");
            }
          })
          .catch((err) => {
            console.log(err);
            //ChangeCursor();
          });
      }
    }
  };

  var b64toBlob = function (b64Data, contentType, sliceSize) {
    contentType = contentType || "";
    sliceSize = sliceSize || 512;
    var byteCharacters = atob(b64Data);
    var byteArrays = [];

    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      var slice = byteCharacters.slice(offset, offset + sliceSize);
      var byteNumbers = new Array(slice.length);
      for (var i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      var byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    var blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };

  function formatDate(date) {
    return (
      [
        date.getFullYear(),
        padTo2Digits(date.getMonth() + 1),
        padTo2Digits(date.getDate()),
      ].join("-") +
      "-" +
      [
        padTo2Digits(date.getHours()),
        padTo2Digits(date.getMinutes()),
        padTo2Digits(date.getSeconds()),
      ].join("-")
    );
  }

  function padTo2Digits(num) {
    return num.toString().padStart(2, "0");
  }
  console.log(care_context_list.display, "test_");
  const StyledTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: "transparent",
      padding: 0,
      maxWidth: 300,
    },
  });
  const onSubmit = (data, e) => {
    e.preventDefault();
    //ChangeCursor();

    // // MODIFIED: Check if ABDM is linked to determine upload type
    // if (!linkToABDM) {
    //   // Use local upload function
    //   handleLocalUpload(data);
    //   return;
    // }

    // Continue with existing ABDM upload logic
    if (Token()) {
      // let reader = new FileReader();
      const file =
        document.querySelector("input[type=file]")?.files?.[0] ||
        attachedFile ||
        null;
      if (!file) {
        toast.error("Please choose a file first!!");
        return;
      }
      const date_prefix = formatDate(new Date());
      const extension = file.name.split(".").pop();
      const fileName =
        file.name.slice(0, 5) + "-" + date_prefix + "." + extension;

      const fileSizeInKB = file.size / 1024;
      if (fileSizeInKB < 20) {
        toast.error("File size must be at least 20KB.");
        return;
      }

      if (fileSizeInKB > 10240) {
        toast.error("File size must not exceed 10MB.");
        return;
      }

      if (file !== undefined) {
        const isDiagUnstructured =
          getValues("health_info_type") === "Quantitative Lab Report (OCR based)" &&
          getValues("data_type") === "Unstructured Data";
        // If Quantitative Lab Report (OCR based) + Unstructured, hit AI upload endpoint and show underwriter view
        if (isDiagUnstructured && isAIEnabled) {
          const aiFormData = new FormData();
          aiFormData.append("patient_id", getValues("patient_id"));
          aiFormData.append("document_type", "diagonstic_report");
          aiFormData.append(
            "carecontext_reference_id",
            getValues("carecontext_ref_id")
          );
          aiFormData.append("file", file);
          axios
            .post(
              `${REACT_APP_BACKEND_ROUTE_URL}process_diagnostic_report/`,
              aiFormData,
              {
                headers: headers,
              }
            )
            .then((response) => {
              if (response.data.status === "Success") {
                handleRefresh();
                toast.success(
                  "File sent for AI processing. Please follow the status in the Status Table"
                );
              } else {
                toast.error(response.data.message);
              }
            })
            .catch((err) => {
              console.log(err);
              toast.error(
                err?.response?.data?.message ||
                err.message ||
                "AI upload failed."
              );
            });
          setSubmitted(true);
          return;
        } else {
          console.log("not isDiagUnstructured");
          const fileSizeInKB = file.size / 1024;
          if (fileSizeInKB < 20) {
            toast.error("File size must be at least 20KB.");
          } else if (fileSizeInKB > 10 * 1024 * 1024) {
            toast.error("File size must not exceed 10MB.");
          } else {
            const mapHiTypes = {
              Prescription: "prescription",
              "Quantitative Lab Report (OCR based)": "diagonstic_report",
              "Diagnostic Report": "diagonstic_report",
              "OP Consultation": "op_consultation",
              "Discharge Summary": "discharge_summary",
              "Immunization Record": "immunization_record",
              "Health Document": "health_document",
              "Wellness Record": "wellness_record",
            };

            var hiType = "";
            for (const key in mapHiTypes) {
              if (key === getValues("health_info_type")) {
                hiType = mapHiTypes[key];
              }
            }
            console.log(hiType);
            const data_type = getValues("data_type");
            var bodyFormData = new FormData();
            if (!linkToABDM) {
              bodyFormData.append("patientABHA", null);
            } else {
              bodyFormData.append(
                "patientABHA",
                getValues("abha_id").endsWith(REACT_APP_SUFFIX)
                  ? getValues("abha_id")
                  : getValues("abha_id") + REACT_APP_SUFFIX
              );
            }

            bodyFormData.append("patientID", getValues("patient_id"));
            bodyFormData.append("HIP_ID", getValues("facility_id"));
            const careContextReferenceIDString = getValues("carecontext_ref_id");
            const careContextReferenceID =
              careContextReferenceIDString.split("_")[0];

            bodyFormData.append("careContextReferenceID", careContextReferenceID);
            console.log("testing...", careContextReferenceID);
            bodyFormData.append("facilityCode", "002347");
            bodyFormData.append("documentType", hiType);
            bodyFormData.append("file", file);

            if (data_type !== undefined && data_type === "Unstructured Data") {
              bodyFormData.append("batch", false);
              bodyFormData.append("structured", false);
              axios
                .post(
                  `${REACT_APP_BACKEND_ROUTE_URL}upload_health_records/`,
                  bodyFormData,
                  {
                    headers: headers,
                  }
                )
                .then((response) => {
                  console.log(response);

                  if (response.data.status === "Success") {
                    toast.success(response.data.message);
                    console.log("Get Values", getValues("carecontext_ref_id"));
                    handleRefresh();
                  } else {
                    const message =
                      response.data.message || "An unknown error occurred.";
                    if (message.includes("corrupted")) {
                      toast.error(
                        "The Facility documents uploaded are corrupted. Please try with a different document."
                      );
                    } else if (message.includes("Virus")) {
                      toast.error(
                        "The Facility documents uploaded has virus. Please try with a different document."
                      );
                    } else {
                      toast.error(message);
                    }
                  }

                  if (
                    sessionStorage.getItem("subscriptionType") ===
                    "ABDM Connector/Adaptor - HIU/HIP" ||
                    sessionStorage.getItem("subscriptionType") ===
                    "ABDM Connector/Adaptor - HIU" ||
                    sessionStorage.getItem("subscriptionType") ===
                    "ABDM Connector/Adaptor - HIP"
                  ) {
                    var requestBody = new FormData();

                    requestBody.append("patientID", getValues("patient_id"));
                    requestBody.append("HIP_ID", getValues("facility_id"));
                    const careContextReferenceIDString =
                      getValues("carecontext_ref_id");
                    const careContextReferenceID =
                      careContextReferenceIDString.split("_")[0];

                    requestBody.append(
                      "careContextReferenceID",
                      careContextReferenceID
                    );
                    console.log("testing...", careContextReferenceID);

                    axios
                      .post(
                        `${REACT_APP_BACKEND_ROUTE_URL}fetch_health_records_for_connector/`,
                        requestBody,
                        {
                          headers: headers,
                        }
                      )
                      .then((res) => {
                        console.log(res.data);
                      })
                      .catch((err) => {
                        console.log(err);
                      });
                  }
                })
                .catch((err) => {
                  console.log(err);
                  toast.error(err);
                  //ChangeCursor();
                });
            } else {
              bodyFormData.append("batch", true);
              bodyFormData.append("structured", true);
              axios
                .post(
                  `${REACT_APP_BACKEND_ROUTE_URL}upload_health_records/`,
                  bodyFormData,
                  {
                    headers: headers,
                  }
                )
                .then((response) => {
                  console.log(response);
                  // toast.success(response.data.message);
                  console.log("Get Values", getValues("carecontext_ref_id"));

                  if (response.data.status === "Success") {
                    toast.success(response.data.message);
                    console.log("Get Values", getValues("carecontext_ref_id"));
                    handleRefresh();
                  } else {
                    const message =
                      response.data.message || "An unknown error occurred.";
                    if (message.includes("corrupted")) {
                      toast.error(
                        "The Facility documents uploaded are corrupted. Please try with a different document."
                      );
                    } else if (message.includes("Virus")) {
                      toast.error(
                        "The Facility documents uploaded has virus. Please try with a different document."
                      );
                    } else {
                      toast.error(message);
                    }
                  }

                  if (
                    sessionStorage.getItem("subscriptionType") ===
                    "ABDM Connector/Adaptor - HIU/HIP" ||
                    sessionStorage.getItem("subscriptionType") ===
                    "ABDM Connector/Adaptor - HIU" ||
                    sessionStorage.getItem("subscriptionType") ===
                    "ABDM Connector/Adaptor - HIP"
                  ) {
                    var requestBody = new FormData();

                    requestBody.append("patientID", getValues("patient_id"));
                    requestBody.append("HIP_ID", getValues("facility_id"));
                    const careContextReferenceIDString =
                      getValues("carecontext_ref_id");
                    const careContextReferenceID =
                      careContextReferenceIDString.split("_")[0];

                    requestBody.append(
                      "careContextReferenceID",
                      careContextReferenceID
                    );
                    console.log(
                      "testing...",
                      careContextReferenceID,
                      careContextReferenceIDString
                    );

                    axios
                      .post(
                        `${REACT_APP_BACKEND_ROUTE_URL}fetch_health_records_for_connector/`,
                        requestBody,
                        {
                          headers: headers,
                        }
                      )
                      .then((res) => {
                        console.log(res.data);
                      })
                      .catch((err) => {
                        console.log(err);
                      });
                  }
                  //ChangeCursor();
                })
                .catch((err) => {
                  console.log(err);
                  toast.error(err);
                  //ChangeCursor();
                });
            }
          }
        }
      } else {
        toast.error("Please choose a file first!!");
      }
    }
    setSubmitted(true);
  };

  const upload = watch("uploaded_file");
  const abha_id_field =
    watch("abha_id") !== undefined &&
    (watch("abha_id").endsWith(REACT_APP_SUFFIX)
      ? watch("abha_id")
      : watch("abha_id") + REACT_APP_SUFFIX);
  console.log("Abha id field", abha_id_field);
  const patient_id_field = watch("patient_id");
  const facility_id_field = watch("facility_id");
  const healthInfoType = watch("health_info_type");
  const isQuantitativeLabOcr =
    healthInfoType === "Quantitative Lab Report (OCR based)";
  // Quantitative Lab Report (OCR based) → PDF only; all other types → ZIP only.
  const acceptedFileTypes = isQuantitativeLabOcr ? ".pdf" : ".zip";
  const isDiagUnstructured =
    isQuantitativeLabOcr && watch("data_type") === "Unstructured Data";

  const filteredS3Files = useMemo(() => {
    const selectable = (s3Files || []).filter((file) =>
      isSelectableS3Doc(file, acceptedFileTypes),
    );
    const q = s3SearchQuery.trim().toLowerCase();
    if (!q) return selectable;
    return selectable.filter((file) => {
      const name = String(file.file_name || "").toLowerCase();
      const date = String(file.upload_date || "").toLowerCase();
      const key = String(file.s3_key || "").toLowerCase();
      return name.includes(q) || date.includes(q) || key.includes(q);
    });
  }, [s3Files, s3SearchQuery, acceptedFileTypes]);

  useEffect(() => {
    if (
      selectedS3File &&
      !filteredS3Files.some(
        (f) => f.s3_key && f.s3_key === selectedS3File.s3_key,
      )
    ) {
      setSelectedS3File(null);
    }
  }, [filteredS3Files, selectedS3File]);

  const sortDocumentsByRecent = (documents) =>
    [...(documents || [])].sort((a, b) => {
      const ta = new Date(
        a?.upload_date ?? a?.created_at ?? a?.uploaded_at ?? 0,
      ).getTime();
      const tb = new Date(
        b?.upload_date ?? b?.created_at ?? b?.uploaded_at ?? 0,
      ).getTime();
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    });

  const assignFileToUploadInput = (file) => {
    const input = document.getElementById("contained-button-file");
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    if (input) {
      input.files = dataTransfer.files;
      try {
        input.dispatchEvent(new Event("change", { bubbles: true }));
      } catch (e) {
        // ignore
      }
    }
    // Keep a stable File reference — RHF FileList can be cleared on re-render.
    setAttachedFile(file);
    setFileNameSelected([file]);
    setValue("uploaded_file", dataTransfer.files, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    clearErrors("uploaded_file");
    setSubmitted(false);
  };

  const clearAttachedFile = () => {
    setAttachedFile(null);
    setFileNameSelected("");
    setIsPreviewModalOpen(false);
    setPreviewModalFile(null);
    const input = document.getElementById("contained-button-file");
    if (input) input.value = "";
    setValue("uploaded_file", undefined, { shouldValidate: true });
  };

  // Drop attached file when Health Information Type changes accept rules.
  useEffect(() => {
    if (!attachedFile) return;
    if (!matchesAcceptedExtension(attachedFile.name, acceptedFileTypes)) {
      clearAttachedFile();
      toast.info(
        `File cleared — only ${acceptedFileTypes} is allowed for this Health Information Type.`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFileTypes]);

  const fetchS3Documents = async () => {
    const patientId = String(getValues("patient_id") || "").trim();
    if (!patientId) {
      toast.error("Please enter Patient ID first");
      return;
    }
    if (!Token()) return;

    setIsFetchingS3Docs(true);
    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get-patient-uploaded-documents/`,
        {
          patient_id: patientId,
          upload_type: "doc",
          include_base64: true,
        },
        {
          headers: {
            ...headers,
            "App-Mode": sessionStorage.getItem("APP_MODE") || "",
            "Org-Code": sessionStorage.getItem("ORG_CODE") || "",
            Username: sessionStorage.getItem("userName") || "",
            "User-Access-Key": sessionStorage.getItem("USER_ACCESS_KEY") || "",
            "Content-Type": "application/json",
          },
        },
      );

      if (
        response.data?.status === "Success" ||
        response.data?.is_valid === true ||
        Array.isArray(response.data?.documents)
      ) {
        setS3Files(sortDocumentsByRecent(response.data.documents || []));
      } else {
        setS3Files([]);
        toast.error(
          response.data?.message || "Failed to fetch uploaded documents.",
        );
      }
    } catch (error) {
      console.error("Error fetching S3 documents:", error);
      setS3Files([]);
      toast.error(
        error.response?.data?.message || "Error fetching S3 documents.",
      );
    } finally {
      setIsFetchingS3Docs(false);
    }
  };

  const openS3Modal = () => {
    const patientId = String(getValues("patient_id") || "").trim();
    if (!patientId) {
      toast.error("Please enter Patient ID first");
      return;
    }
    setSelectedS3File(null);
    setS3SearchQuery("");
    setIsS3ModalOpen(true);
  };

  useEffect(() => {
    if (isS3ModalOpen) {
      fetchS3Documents();
    } else {
      setS3SearchQuery("");
      setSelectedS3File(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isS3ModalOpen]);

  const handleImportS3File = async () => {
    if (!selectedS3File) {
      toast.error("Please select a document");
      return;
    }
    if (!isSelectableS3Doc(selectedS3File, acceptedFileTypes)) {
      toast.error(
        `Only ${acceptedFileTypes} files can be imported for the selected Health Information Type.`,
      );
      return;
    }
    setIsImportingS3(true);
    try {
      const mimeType = guessUploadMimeType(
        selectedS3File.file_name,
        selectedS3File.content_type,
      );
      const dataUrl = fileBase64ToDataUrl(selectedS3File);
      let file;
      if (dataUrl) {
        file = await dataUrlToFile(
          dataUrl,
          selectedS3File.file_name || "document",
          mimeType,
        );
      } else if (selectedS3File.url) {
        file = await urlToFile(
          selectedS3File.url,
          selectedS3File.file_name || "document",
          mimeType,
        );
      } else {
        throw new Error("No file data available for selected document");
      }

      // Normalize extension-less PDF/ZIP names for the file picker accept filter.
      if (!matchesAcceptedExtension(file.name, acceptedFileTypes)) {
        const ext = String(acceptedFileTypes).replace(".", "") || "bin";
        const base = String(file.name || "document").replace(/\.[^.]+$/, "");
        file = new File([file], `${base}.${ext}`, {
          type:
            ext === "pdf"
              ? "application/pdf"
              : ext === "zip"
                ? "application/zip"
                : file.type,
        });
      }

      assignFileToUploadInput(file);
      setIsS3ModalOpen(false);
      toast.success(`Attached: ${file.name}`);
    } catch (error) {
      console.error("S3 import failed:", error);
      toast.error(
        error.message || "Could not import selected document. Please try again.",
      );
    } finally {
      setIsImportingS3(false);
    }
  };

  useEffect(() => {
    // MODIFIED: Only auto-populate from ABDM when linked
    if (linkToABDM) {
      var patientData = patientList.filter(
        (item, index) => item.abha_id === abha_id_field
      );
      console.log("patientData", patientData);
      if (patientData !== undefined && patientData.length > 0) {
        setValue("patient_id", patientData[0].patient_id);
        setCurrentPatient(patientData[0]);
      } else {
        setValue("patient_id", "");
      }
    }
    console.log(patient_id_field);
  }, [abha_id_field, linkToABDM]);

  useEffect(() => {
    if (care_context_list.length > 0) {
      reset({
        carecontext_ref_id: undefined,
      });
      setCare_context_list([]);
    }
  }, [abha_id_field, patient_id_field, facility_id_field]);

  useEffect(() => {
    if (upload !== undefined && upload.length > 0) {
      const file = upload[0];
      setFileNameSelected(upload);
      setAttachedFile(file);
    }
    // Do not clear attachedFile when RHF FileList becomes empty after import.
  }, [upload]);

  const selectedUploadFile =
    attachedFile ||
    (fileNameSelected && fileNameSelected.length > 0
      ? fileNameSelected[0]
      : null);
  const canPreviewSelectedFile = Boolean(selectedUploadFile);

  const openLocalFilePreview = () => {
    if (!selectedUploadFile) return;
    setPreviewModalFile(selectedUploadFile);
    setIsPreviewModalOpen(true);
  };

  const closeFilePreview = () => {
    setIsPreviewModalOpen(false);
    setPreviewModalFile(null);
  };

  const openS3DocPreview = async (doc, event) => {
    if (event) event.stopPropagation();
    try {
      const mimeType = guessUploadMimeType(doc.file_name, doc.content_type);
      const dataUrl = fileBase64ToDataUrl(doc);
      let file;
      if (dataUrl) {
        file = await dataUrlToFile(
          dataUrl,
          doc.file_name || "document",
          mimeType,
        );
      } else if (doc.url) {
        file = await urlToFile(doc.url, doc.file_name || "document", mimeType);
      } else {
        toast.error("Preview data not available for this document");
        return;
      }
      setPreviewModalFile(file);
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error("S3 preview failed:", error);
      toast.error("Could not open preview for this document");
    }
  };

  const get_care_contexts = () => {
    console.log(getValues("patient_id"));

    // MODIFIED: Handle different logic based on ABDM link status
    if (!linkToABDM) {
      // For local upload, just need patient ID
      if (!getValues("patient_id")) {
        toast.error("Please fill Patient ID");
        return;
      }
      fetch_by_patient_id();
      return;
    }

    // Original ABDM logic continues here
    if (getValues("patient_id").length <= 0 && getValues("abha_id") <= 0) {
      toast.error("Please fill either ABHA Address or Patient ID");
    } else if (getValues("abha_id").length > 0) {
      //ChangeCursor();
      var post_data = {};
      if (Token()) {
        post_data.filter_type = "patient_abha";
        post_data.filter_details = {};
        post_data.filter_details.patient_abha = getValues("abha_id").endsWith(
          REACT_APP_SUFFIX
        )
          ? getValues("abha_id")
          : getValues("abha_id") + REACT_APP_SUFFIX;

        axios
          .post(
            `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
            JSON.stringify(post_data),
            {
              headers: headers,
            }
          )
          .then((res) => {
            if (res.data.status !== "Failed") {
              console.log("SUCES", res);
              if (res.data.care_contenxts.length <= 0) {
                toast.error("No Visit Purpose.");
              }
              setCare_context_list(res.data.care_contenxts);
              setSelectedRefId(
                res.data.care_contenxts.length > 0
                  ? res.data.care_contenxts[0].care_context_reference
                  : null
              );
            } else {
              if (getValues("patient_id").length > 0) {
                fetch_by_patient_id();
              } else {
                toast.error("Visit Purpose Fetch Failed!!");
              }
            }
            //ChangeCursor();
          });
      }
    } else {
      fetch_by_patient_id();
    }
  };

  const fetch_by_patient_id = () => {
    var post_data = {};
    //ChangeCursor();
    if (Token()) {
      post_data.filter_type = "patient_id";
      post_data.filter_details = {};
      post_data.filter_details.patient_id = getValues("patient_id");

      axios
        .post(
          `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
          JSON.stringify(post_data),
          {
            headers: headers,
          }
        )
        .then((res) => {
          if (res.data.status !== "Failed") {
            console.log("SUCES", res);
            if (res.data.care_contenxts.length <= 0) {
              toast.error("No Visit Purpose.");
            }
            setCare_context_list(res.data.care_contenxts);
            setSelectedRefId(
              res.data.care_contenxts.length > 0
                ? res.data.care_contenxts[0].care_context_reference
                : null
            );
          } else {
            toast.error("Visit Purpose Fetch Failed!!");
          }
          //ChangeCursor();
        });
    }
  };

  const navigate = (e) => {
    history.push(`/${e.target.id}`);
  };

  console.log("care context list", care_context_list, selectedRefId);

  // console.log(getValues("abha_id").length)
  if (aiResponse) {
    return (
      <DiagnosticReportsUnderwriter
        initialData={aiResponse}
        setAiResponse={setAiResponse}
        selectedPatient={selectedPatient}
        handleRefresh={handleRefresh}
      />
    );
  }
  return (
    <div>
      <UploadReprortTable
        refresh={refresh}
        setAiResponse={setAiResponse}
        setSelectedPatient={setSelectedPatient}
      />
      <Box
        sx={{
          width: { xs: "95%", sm: "60%", md: "60%" },
          maxWidth: "900px",
          overflowY: "auto",
          px: 0,
          py: { xs: 2, sm: 3 },
          background: "transparent",
          boxShadow: "none",
          border: "none",
          margin: "auto",
        }}
      >
        <Card
          style={{
            backgroundColor: "#FDFDFD",
            borderRadius: "12px",
            width: "100%",
            border: "1px solid #E5E7EB",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.08)",
            padding: 0,
          }}
        >
          {/* Header bar */}
          <Box
            sx={{
              backgroundColor: "#213360",
              color: "#FFFFFF",
              px: "24px",
              py: "18px",
              minHeight: "72px",
              display: "flex",
              alignItems: "center",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              borderBottom: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, color: "#FFFFFF", fontSize: "24px" }}
            >
              Upload Report
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 3 }}>
            <form
              className="contact-panel__form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="row" style={{ alignItems: "center" }}>
                <div className="col-sm-6 col-md-6 col-lg-6">
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#0E3F66" }}
                  >
                    Link health records with ABHA for interoperability
                  </Typography>
                </div>
                <div
                  className="col-sm-6 col-md-6 col-lg-6"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={linkToABDM}
                        onChange={handleLinkToggle}
                        style={{ verticalAlign: "middle" }}
                        disabled={!isABDMEnabled}
                      />

                      <span style={{ lineHeight: 1 }}>Link to ABDM</span>
                    </label>
                  </div>
                </div>
                {/* <div className="col-sm-6 col-md-6 col-lg-6"></div> */}

                <div className="col-sm-12 col-md-12 col-lg-12">
                  <div className="form-group">
                    <label> EnterABHA </label>
                    {/* <i className="icon-news form-group-icon"></i> */}
                    <input
                      type="text"
                      className="form-control"
                      placeholder="ABHA Address"
                      name="abha_id"
                      disabled={!linkToABDM}
                      {...register("abha_id", {
                        required: linkToABDM
                          ? "ABHA Address is required."
                          : false,
                        minLength: linkToABDM
                          ? {
                            value: 4,
                            message: "Minimum Length is 4.",
                          }
                          : undefined,
                        pattern: linkToABDM
                          ? {
                            value: process.env.abhaIdRegex,
                            message:
                              "Alphanumeric characters only. Must be between 8 to 18 characters long. Special characters allowed: dot (.) or underscore (_), which cannot be in the beginning or at the end.",
                          }
                          : undefined,
                      })}
                    />
                    {errors.abha_id && (
                      <p className="errorMsg">{errors.abha_id.message}</p>
                    )}
                  </div>
                </div>
                <div className="col-sm-6 col-md-6 col-lg-6">
                  <div className="form-group">
                    <label> Search by Phone Number </label>
                    {/* <i className="icon-news form-group-icon"></i> */}
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Phone Number"
                      name="phone_number"
                      disabled={linkToABDM}
                      {...register("phone_number")}
                    />
                  </div>
                </div>

                <div className="col-sm-6 col-md-6 col-lg-6">
                  <div className="form-group">
                    <label> Search by Phone Number </label>
                    <button
                      type="button"
                      onClick={searchPatientByPhone}
                      className="btn btn__secondary btn__rounded btn__block "
                      disabled={linkToABDM || isSearchingPatient}
                    >
                      <span>
                        {isSearchingPatient ? "Searching..." : "Search Patient"}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Patient ID field (auto-filled or dropdown on multiple results) */}
                <div className="col-sm-6 col-md-6 col-lg-6">
                  <div className="form-group">
                    <label> Enter Patient ID </label>
                    {!linkToABDM && phoneSearchResults.length > 1 ? (
                      <select
                        className="form-control"
                        name="patient_id"
                        {...register("patient_id", {
                          // required: "Patient ID is required.",
                        })}
                        onChange={handleSelectPatientFromResults}
                      >
                        <option value="">Select Patient</option>
                        {phoneSearchResults.map((item, index) => (
                          <option key={index} value={item.patient_id}>
                            {item.patient_id} - {displayPatientName(item.name)}{" "}
                            {item.abha_id ? `(${item.abha_id})` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Patient ID"
                        name="patient_id"
                        {...register("patient_id", {
                          // required: "Patient ID is required.",
                        })}
                        disabled={linkToABDM}
                      />
                    )}
                    {errors.patient_id && (
                      <p className="errorMsg" style={{ color: "red" }}>
                        {errors.patient_id.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-sm-6 col-md-6 col-lg-6">
                  <div className="form-group">
                    <label> Facility ID </label>
                    {/* <i className="icon-news form-group-icon"></i> */}
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Facility ID"
                      name="facility_id"
                      {...register("facility_id", {
                        required: "Facility ID is required.",
                      })}
                      disabled
                    />
                    {errors.facility_id && (
                      <p className="errorMsg">{errors.facility_id.message}</p>
                    )}
                  </div>
                </div>
                {/* {care_context_list !== undefined &&
              care_context_list.length > 0 ? (
                <div className="col-sm-6 col-md-6 col-lg-6">
                  
                  <div className="form-group">
                    <i className="icon-user form-group-icon"></i>
                    <select
                      className="form-control"
                      name="carecontext_ref_id"
                      {...register("carecontext_ref_id", {
                        required: "Consultation type is required.",
                      })}
                    >
                      {care_context_list.map((item, index) => (
                        <option value={item.care_context_reference}>
                          {item.care_context_reference}
                        </option>
                      ))}
                    </select>
                    {errors.carecontext_ref_id && (
                      <p className="errorMsg">
                        {errors.carecontext_ref_id.message}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="col-sm-6 col-md-6 col-lg-6">
                  <button
                    type="button"
                    onClick={() => get_care_contexts()}
                    className="btn btn__secondary btn__rounded btn__block  mt-12"
                  >
                    <span>Get Visit Purpose</span>{" "}
                    <i className="icon-arrow-right"></i>
                  </button>
                </div>
              )} */}
                {care_context_list !== undefined &&
                  care_context_list.length > 0 ? (
                  <>
                    <div className="col-xs-10 col-sm-6 col-md-6 col-lg-6">
                      <div className="form-group">
                        <i className="icon-user form-group-icon"></i>
                        <select
                          className="form-control"
                          placeholder="Select Visit Purpose"
                          name="carecontext_ref_id"
                          {...register("carecontext_ref_id", {
                            required: "Consultation type is required.",
                          })}
                          style={{ width: "100%" }} // Ensures full width
                          onChange={(e) => setSelectedRefId(e.target.value)}
                        >
                          {care_context_list.map((item, index) => (
                            <option
                              key={index}
                              value={item.care_context_reference}
                            >
                              {item.care_context_reference}
                            </option>
                          ))}
                        </select>
                        {errors.carecontext_ref_id && (
                          <p className="errorMsg">
                            {errors.carecontext_ref_id.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div
                      className="col-xs-2 col-sm-1 col-md-1 col-lg-1"
                      style={{ margin: "auto" }}
                    >
                      {care_context_list.length > 0 && (
                        <div
                          type="button"
                          onClick={handleCopy}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            margin: "auto",
                            color: copied ? "green" : "black",
                          }}
                        >
                          <ContentCopyIcon />
                          {copied && (
                            <span style={{ marginLeft: "5px" }}>Copied!</span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="col-sm-6 col-md-6 col-lg-6">
                    <button
                      type="button"
                      onClick={() => get_care_contexts()}
                      className="btn btn__secondary btn__rounded btn__block  mt-12"
                    >
                      <span>Get Visit Purpose</span>{" "}
                      {/* <i className="icon-arrow-right"></i> */}
                    </button>
                  </div>
                )}
                {/* <div className="col-sm-4 col-md-4 col-lg-4">
                    <div className="form-group" onChange={() => onChangeHIP()}>
                      <label>Health Information Type</label>
                      {HIPTypes.map((item, index) => (
                        <div class="form-check">
                          <input
                            type="radio"
                            class="form-check-input"
                            placeholder="Select Health Information Type"
                            value={item}
                            name="health_info_type"
                            {...register("health_info_type", {
                              required: "Health Info Type is required.",
                            })}
                          />
                          <label
                            class="form-check-label"
                            for="flexRadioDefault2"
                          >
                            {item}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div> */}

                <div className="col-sm-12">
                  <div className="form-group" onChange={() => onChangeHIP()}>
                    <label className="d-block mb-2 mt-2">
                      Health Information Type
                    </label>
                    <div className="row">
                      {HIPTypes.map((item, index) => (
                        <div key={index} className="col-6 col-md-4 col-lg-3">
                          <div className="form-check">
                            <input
                              style={{ marginTop: "0.3rem" }}
                              type="radio"
                              className="form-check-input"
                              id={`hip-${index}`}
                              value={item}
                              name="health_info_type"
                              {...register("health_info_type", {
                                required: "Health Info Type is required.",
                              })}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`hip-${index}`}
                              style={{ fontWeight: 500, fontSize: "12px" }}
                            >
                              {item}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* <div className="col-sm-3 col-md-3 col-lg-3"> */}
                {/* <div className="form-group">
                  <label>File Types (XLSX)</label>
                  {fileTypes !== undefined
                    ? fileTypes.map((item, index) => (
                        <div class="form-check">
                          <input
                            type="checkbox"
                            multiple
                            class="form-check-input"
                            placeholder="File Types"
                            value={item}
                            name="file_type"
                            {...register("file_type")}
                          />
                          <label
                            class="form-check-label"
                            for="flexRadioDefault2"
                            style={{ fontWeight: 400, fontSize: "15px" }}
                          >
                            {item}
                          </label>
                        </div>
                      ))
                    : []}
                </div> */}
                {/* </div> */}
                <div className="col-sm-12 col-md-12 col-lg-12">
                  <button
                    type="button"
                    onClick={() => downloadTemplate()}
                    className="btn btn__secondary btn__rounded btn__block btn__xhight mt-10 mb-10"
                  >
                    <span>Download Template</span>{" "}
                    {/* <i className="icon-arrow-right"></i> */}
                  </button>
                  <div className="contact-result"></div>
                </div>
                <div className="col-sm-6 col-md-6 col-lg-6">
                  <div className="form-group">
                    <label>
                      Data Type <span style={{ color: "red" }}>*</span>
                    </label>

                    {DataTypes.map((item, index) => (
                      <div key={index} className="form-check">
                        <input
                          id={`data-type-${index}`}
                          style={{ marginTop: "0.3rem" }}
                          type="radio"
                          className="form-check-input"
                          defaultChecked={item === "Structured Data"}
                          value={item}
                          name="data_type"
                          {...register("data_type")}
                        />

                        <label
                          htmlFor={`data-type-${index}`}
                          className="form-check-label"
                          style={{ fontWeight: 500, fontSize: "12px" }}
                        >
                          {item}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-sm-6 col-md-6 col-lg-6">
                  <label>
                    File Upload is required
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <label
                    htmlFor="contained-button-file"
                    id="button-upload"
                    className="btn btn__secondary btn__rounded btn__block  btn__xhight"
                  >
                    <input
                      type="file"
                      id="contained-button-file"
                      hidden
                      accept={acceptedFileTypes}
                      {...register("uploaded_file", {
                        validate: () => {
                          const rhfFiles = getValues("uploaded_file");
                          const hasRhfFile =
                            rhfFiles &&
                            typeof rhfFiles.length === "number" &&
                            rhfFiles.length > 0;
                          const hasInputFile =
                            document.getElementById("contained-button-file")
                              ?.files?.length > 0;
                          if (attachedFile || hasRhfFile || hasInputFile) {
                            return true;
                          }
                          return "File Upload is required.";
                        },
                        onChange: (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setAttachedFile(file);
                            setFileNameSelected([file]);
                            clearErrors("uploaded_file");
                            setSubmitted(false);
                          } else {
                            clearAttachedFile();
                          }
                        },
                      })}
                    />
                    Choose file
                  </label>
                  {String(patient_id_field || "").trim() !== "" && (
                    <button
                      type="button"
                      onClick={openS3Modal}
                      style={{
                        marginTop: "8px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#fff",
                        backgroundColor: "#0E3F66",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Browse Uploaded Documents
                    </button>
                  )}
                  {errors.uploaded_file &&
                    !submitted &&
                    !selectedUploadFile && (
                      <p className="errorMsg">{errors.uploaded_file.message}</p>
                    )}
                </div>
                <div className="col-sm-6 col-md-6 col-lg-6"></div>
                <div
                  className="col-sm-6 col-md-6 col-lg-6"
                  style={{
                    margin: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  {canPreviewSelectedFile ? (
                    <span style={{ fontWeight: 600, wordBreak: "break-all" }}>
                      Attached: {selectedUploadFile.name}
                    </span>
                  ) : null}
                  {canPreviewSelectedFile && (
                    <Typography
                      component="span"
                      onClick={openLocalFilePreview}
                      sx={{
                        color: "#0E3F66",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Preview
                    </Typography>
                  )}
                  {canPreviewSelectedFile && (
                    <Typography
                      component="span"
                      onClick={clearAttachedFile}
                      sx={{
                        color: "#FF3B30",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Remove
                    </Typography>
                  )}
                </div>
                {isAIEnabled && (
                  <div className="col-sm-12 col-md-12 col-lg-12">
                    <Box
                      sx={{
                        backgroundColor: "#FFF8E1",
                        border: "1px solid #FFECB3",
                        borderRadius: "6px",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        color: "#795548",
                        fontSize: "0.875rem",
                        lineHeight: 1.4,
                        marginBottom: 1.5,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            wordBreak: "break-word",
                            mb: 0.75,
                          }}
                        >
                          Quantitative Lab Report (OCR based) is best optimized for:
                        </Typography>
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            pl: 2.25,
                            fontSize: "0.8rem",
                            "& li": { mb: 0.35 },
                          }}
                        >
                          <Typography component="li" sx={{ fontSize: "0.8rem" }}>
                            Clinical pathology (biochemistry, hematology)
                          </Typography>
                          <Typography component="li" sx={{ fontSize: "0.8rem" }}>
                            Endocrinology (hormone panels)
                          </Typography>
                          <Typography component="li" sx={{ fontSize: "0.8rem" }}>
                            Structured immunology reports
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.8rem", mt: 1 }}>
                          For other kinds of reports, please choose Diagnostic Report.
                        </Typography>
                      </Box>
                      {isDiagUnstructured && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", lg: "row" },
                            alignItems: { xs: "flex-start", lg: "center" },
                            justifyContent: "space-between",
                            gap: 1,
                            pt: 1.5,
                            borderTop: "1px solid #FFECB3",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <span style={{ fontSize: "12px", flexShrink: 0 }}>
                              ⚠️
                            </span>
                            <Typography
                              component="span"
                              sx={{
                                flex: 1,
                                wordBreak: "break-word",
                                fontSize: "0.8rem",
                              }}
                            >
                              Quantitative Lab Report PDFs over 3–4 pages may
                              cause OCR errors or missing values.
                            </Typography>
                          </Box>
                          {canPreviewSelectedFile && (
                            <Typography
                              component="span"
                              onClick={openLocalFilePreview}
                              sx={{
                                color: "#0E3F66",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                mt: { xs: 0.5, lg: 0 },
                                ml: { xs: 0, lg: 1.5 },
                                flexShrink: 0,
                              }}
                            >
                              Preview
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </div>
                )}
                <UploadReportFilePreview
                  open={isPreviewModalOpen}
                  file={previewModalFile}
                  fileName={previewModalFile?.name}
                  onClose={closeFilePreview}
                />

                <Modal
                  open={isS3ModalOpen}
                  onClose={() => setIsS3ModalOpen(false)}
                  aria-labelledby="upload-report-s3-docs"
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: { xs: "95%", md: "720px" },
                      maxHeight: "85vh",
                      bgcolor: "background.paper",
                      borderRadius: "10px",
                      boxShadow: 24,
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography
                      id="upload-report-s3-docs"
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: "#0E3F66" }}
                    >
                      Browse Uploaded Documents
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
                      Showing only {acceptedFileTypes} files (selectable for
                      current Health Information Type) for patient{" "}
                      {String(getValues("patient_id") || "").trim() || "—"}.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by date or filename..."
                      value={s3SearchQuery}
                      onChange={(e) => setS3SearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#999" }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 220,
                        maxHeight: "50vh",
                        overflowY: "auto",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        p: 1,
                      }}
                    >
                      {isFetchingS3Docs ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            my: 6,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : s3Files.length === 0 ? (
                        <Typography
                          sx={{
                            textAlign: "center",
                            color: "#94a3b8",
                            my: 6,
                            fontStyle: "italic",
                          }}
                        >
                          No uploaded documents found for this patient.
                        </Typography>
                      ) : filteredS3Files.length === 0 ? (
                        <Typography
                          sx={{
                            textAlign: "center",
                            color: "#94a3b8",
                            my: 6,
                            fontStyle: "italic",
                          }}
                        >
                          {s3SearchQuery.trim()
                            ? "No documents match your search."
                            : `No ${acceptedFileTypes} documents found. Only ${acceptedFileTypes} can be imported for the selected Health Information Type.`}
                        </Typography>
                      ) : (
                        filteredS3Files.map((file) => {
                          const isSelected =
                            selectedS3File?.s3_key &&
                            selectedS3File.s3_key === file.s3_key;
                          const kind = detectUploadedDocKind(file);
                          const isPdf = kind === "pdf";
                          const previewSrc = getS3PreviewSrc(file);
                          const showImageThumb =
                            kind === "image" &&
                            isImageUploadName(
                              file.file_name,
                              file.content_type,
                            );
                          return (
                            <Box
                              key={file.s3_key || file.file_name}
                              onClick={() => setSelectedS3File(file)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                p: 1.25,
                                mb: 1,
                                borderRadius: "8px",
                                cursor: "pointer",
                                border: isSelected
                                  ? "2px solid #21cdc0"
                                  : "1px solid #E5E7EB",
                                backgroundColor: isSelected
                                  ? "#F0FDFA"
                                  : "#fff",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  flexShrink: 0,
                                  borderRadius: "6px",
                                  overflow: "hidden",
                                  bgcolor: "#f3f4f6",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "1px solid #E5E7EB",
                                }}
                              >
                                {showImageThumb && previewSrc ? (
                                  <img
                                    src={previewSrc}
                                    alt={file.file_name || "preview"}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <InsertDriveFileIcon
                                    sx={{
                                      color: isPdf ? "#ff5252" : "#0E3F66",
                                      fontSize: 32,
                                    }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={file.file_name}
                                >
                                  {file.file_name || "document"}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "0.72rem", color: "#6b7280" }}
                                >
                                  {file.upload_date || "—"}
                                </Typography>
                              </Box>
                              <Typography
                                component="span"
                                onClick={(e) => openS3DocPreview(file, e)}
                                sx={{
                                  color: "#0E3F66",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  fontSize: "0.8rem",
                                  flexShrink: 0,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Preview
                              </Typography>
                            </Box>
                          );
                        })
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.5,
                      }}
                    >
                      <button
                        type="button"
                        className="btn"
                        style={{
                          backgroundColor: "#FF3B30",
                          color: "#fff",
                          borderRadius: "8px",
                          padding: "8px 16px",
                          border: "none",
                        }}
                        onClick={() => setIsS3ModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={{
                          backgroundColor:
                            !selectedS3File || isImportingS3
                              ? "#9ca3af"
                              : "#0E3F66",
                          color: "#fff",
                          borderRadius: "8px",
                          padding: "8px 16px",
                          border: "none",
                          cursor:
                            !selectedS3File || isImportingS3
                              ? "not-allowed"
                              : "pointer",
                        }}
                        disabled={!selectedS3File || isImportingS3}
                        onClick={handleImportS3File}
                      >
                        {isImportingS3 ? "Importing..." : "Import Selected"}
                      </button>
                    </Box>
                  </Box>
                </Modal>

                <div className="col-sm-10 col-md-10 col-lg-10">
                  <button
                    type="submit"
                    className="btn btn__secondary btn__rounded btn__block btn__xhight mt-10"
                  >
                    <span>Add Health Data</span>{" "}
                    {/* <i className="icon-arrow-right"></i> */}
                  </button>
                  <div className="contact-result"></div>
                </div>
                <div
                  className="col-sm-2 col-md-2 col-lg-2"
                  style={{ margin: "auto" }}
                >
                  <StyledTooltip
                    arrow
                    placement="right"
                    title={
                      <Box
                        sx={{
                          backgroundColor: "#21cdc0",
                          p: 2,
                          borderRadius: "8px",
                          boxShadow: "0px 0px 0px white",
                          border: "none",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", color: "#fff", mb: 1 }}
                        >
                          Excel Upload Instructions
                        </Typography>
                        <List sx={{ color: "#fff", pl: 2 }}>
                          <ListItem>
                            Columns should be as it is in the template
                          </ListItem>
                          <ListItem>
                            Zip the master and details files along with
                            attachments (if any), then upload the zip file
                          </ListItem>
                          <ListItem>
                            The master file must contain one row with the
                            patient's details as per the template
                          </ListItem>
                          <ListItem>
                            The details file can have multiple rows, maintaining
                            consistency throughout
                          </ListItem>
                        </List>
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ color: "#0E3F66", fontSize: 20 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "#0E3F66", fontWeight: "bold" }}
                      >
                        HELP
                      </Typography>
                    </Box>
                  </StyledTooltip>
                </div>
                <div className="col-12">
                  <small className="form-text text-muted mt-2">
                    <span style={{ color: "red" }}>*</span> Only{" "}
                    <strong>{acceptedFileTypes}</strong> files are allowed for
                    upload.
                  </small>
                </div>
              </div>
            </form>
          </Box>
        </Card>
      </Box>
    </div>
  );
}

export default UploadReport;
